import type {
  AutomationCauseId,
  AutomationFullRunDto,
  AutomationFullRunGroup,
  AutomationFullRunSource,
  AutomationFullRunSourceDef,
} from '@tms/shared';
import { Link, useNavigate } from '@tanstack/react-router';
import { AlertTriangle, ChevronDown, ChevronRight, Layers, Loader, Play } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { PageHeader } from '../../../components/layout/PageHeader';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CollapsibleCard,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  SkeletonRows,
  Stat,
  Table,
  TableWrap,
  Th,
} from '../../../components/ui';
import { useFormat, useT } from '../../../i18n';
import { cn } from '../../../lib/cn';
import {
  useAutomationCan,
  useFullRun,
  useFullRuns,
  useKnownDefects,
  useStartFullRun,
} from '../hooks';
import { AutomationStatusBadge } from '../ui';
import { useRunsErrorText } from './format';

/**
 * «Full run»: every project in one go, and a summary of whose side each
 * failure is on — our backend, Colvir, the stand, test data or the run itself.
 * Known failures carry their number from the QA defect register
 * (TestRun src/pages/FullRunPage.tsx; the server classifies).
 */

const SOURCE_STYLES: Record<
  AutomationFullRunSource,
  { bar: string; text: string; border: string }
> = {
  backend: { bar: 'bg-fail', text: 'text-fail', border: 'border-fail' },
  colvir: { bar: 'bg-block', text: 'text-block', border: 'border-block' },
  mixed: { bar: 'bg-block/60', text: 'text-block', border: 'border-block/60' },
  external: { bar: 'bg-accent', text: 'text-accent-fg', border: 'border-accent' },
  stand: { bar: 'bg-skip', text: 'text-skip', border: 'border-skip' },
  data: { bar: 'bg-accent/50', text: 'text-accent-fg', border: 'border-accent/50' },
  ours: { bar: 'bg-ink-subtle', text: 'text-ink-muted', border: 'border-ink-subtle' },
  new: { bar: 'bg-ink', text: 'text-ink', border: 'border-ink' },
};

const FULL_RUN_ENV = 'STAGE';

export function FullRunPage({ fullRunId }: { fullRunId?: string }) {
  const { t } = useT();
  const { dateTime } = useFormat();
  const navigate = useNavigate();
  const errorText = useRunsErrorText();
  const can = useAutomationCan();
  const list = useFullRuns();
  const start = useStartFullRun();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const id = fullRunId ?? list.data?.[0]?.id;

  const open = (runId: string) =>
    void navigate({ to: '/automation/full-run', search: { id: runId } });

  const onStart = () =>
    start.mutate(
      { environment: FULL_RUN_ENV },
      {
        onSuccess: (started) => {
          setConfirmOpen(false);
          const failed = started.runs.filter((r) => !r.pipelineId).length;
          if (failed) {
            toast.warning(
              t('automation.runs.full.startedPartly', {
                started: started.runs.length - failed,
                total: started.runs.length,
              }),
            );
          } else {
            toast.success(t('automation.runs.full.started', { count: started.runs.length }));
          }
          open(started.id);
        },
        onError: (error) => {
          setConfirmOpen(false);
          toast.error(errorText(error));
        },
      },
    );

  return (
    <>
      <PageHeader
        title={t('automation.runs.full.title')}
        description={t('automation.runs.full.subtitle')}
        actions={
          can.run ? (
            <Button
              variant="primary"
              onClick={() => setConfirmOpen(true)}
              loading={start.isPending}
            >
              {start.isPending ? null : <Play className="size-4" aria-hidden="true" />}
              {t('automation.runs.full.start', { env: FULL_RUN_ENV })}
            </Button>
          ) : null
        }
      />

      {list.error ? (
        <Card>
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        </Card>
      ) : !list.data ? (
        <SkeletonRows rows={4} className="p-0" />
      ) : list.data.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Layers className="size-6" />}
            title={t('automation.runs.full.empty')}
            description={t('automation.runs.full.emptyHint', { env: FULL_RUN_ENV })}
          />
        </Card>
      ) : (
        <>
          <div
            className="flex flex-wrap gap-2"
            role="group"
            aria-label={t('automation.runs.full.list')}
          >
            {list.data.slice(0, 10).map((r) => (
              <Button
                key={r.id}
                size="sm"
                variant={r.id === id ? 'primary' : 'ghost'}
                aria-pressed={r.id === id}
                onClick={() => open(r.id)}
                title={[
                  r.by,
                  r.environment,
                  r.failedToStart
                    ? t('automation.runs.full.notStartedCount', { count: r.failedToStart })
                    : '',
                ]
                  .filter(Boolean)
                  .join(' · ')}
              >
                <Layers className="size-3.5" aria-hidden="true" />
                {dateTime(r.startedAt)}
              </Button>
            ))}
          </div>
          {id ? <FullRunView id={id} /> : null}
        </>
      )}

      <KnownDefects fullRunId={id} />

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t('automation.runs.full.confirmTitle', { env: FULL_RUN_ENV })}
        description={t('automation.runs.full.confirmDescription')}
        confirmLabel={t('automation.runs.full.confirm')}
        destructive={false}
        loading={start.isPending}
        onConfirm={onStart}
      />
    </>
  );
}

const FullRunView = ({ id }: { id: string }) => {
  const { t } = useT();
  const { relative } = useFormat();
  const fullRun = useFullRun(id);
  if (fullRun.error) {
    return (
      <Card>
        <ErrorState error={fullRun.error} onRetry={() => void fullRun.refetch()} />
      </Card>
    );
  }
  const fr = fullRun.data;
  if (!fr) return <SkeletonRows rows={4} className="p-0" />;

  const going = fr.projects.filter((p) => p.pipelineId && !p.final).length;
  const totals = fr.totals;
  return (
    <>
      <div className="flex flex-wrap items-center gap-3 text-[13px] text-ink-muted">
        <span>
          {t('automation.runs.full.by', {
            by: fr.by,
            when: relative(fr.startedAt),
            env: fr.environment,
          })}
        </span>
        {going > 0 ? (
          <span className="flex items-center gap-1 text-accent-fg">
            <Loader className="size-3.5 animate-spin" aria-hidden="true" />
            {t('automation.runs.full.going', { count: going, total: fr.projects.length })}
          </span>
        ) : null}
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label={t('automation.runs.full.tests')} value={totals.total} />
        <Stat
          label={t('automation.runs.results.passed')}
          value={totals.passed}
          valueClassName="text-pass"
        />
        <Stat
          label={t('automation.runs.results.failed')}
          value={totals.failed}
          valueClassName={totals.failed ? 'text-fail' : undefined}
        />
        <Stat label={t('automation.runs.results.skipped')} value={totals.skipped} />
      </div>

      {totals.failed > 0 ? <SourceBar fr={fr} /> : null}
      <ProjectsTable fr={fr} />
      {totals.failed > 0 ? <Groups fr={fr} /> : null}

      <p className="text-[12px] text-ink-subtle">{t('automation.runs.full.howSources')}</p>
    </>
  );
};

/** The bar and legend: each source's share among the failures. */
const SourceBar = ({ fr }: { fr: AutomationFullRunDto }) => {
  const { t } = useT();
  const present = fr.sources.filter((s) => fr.totals.bySource[s.id] > 0);
  return (
    <Card>
      <CardHeader title={t('automation.runs.full.whoseSide')} />
      <CardBody className="space-y-3">
        <div className="flex h-2.5 overflow-hidden rounded-md bg-sunken" aria-hidden="true">
          {present.map((s) => (
            <div
              key={s.id}
              title={`${s.label}: ${fr.totals.bySource[s.id]}`}
              className={cn('min-w-1', SOURCE_STYLES[s.id].bar)}
              style={{ flex: `${fr.totals.bySource[s.id]} 0 0` }}
            />
          ))}
        </div>
        <ul className="flex flex-wrap gap-x-4 gap-y-2 text-[13px]">
          {present.map((s) => (
            <li key={s.id} className="flex items-center gap-2" title={s.hint}>
              <span
                className={cn('size-2.5 shrink-0 rounded-sm', SOURCE_STYLES[s.id].bar)}
                aria-hidden="true"
              />
              {s.label} <b>{fr.totals.bySource[s.id]}</b>
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  );
};

const SourceChips = ({
  bySource,
  sources,
}: {
  bySource: Record<AutomationFullRunSource, number>;
  sources: AutomationFullRunSourceDef[];
}) => {
  const present = sources.filter((s) => bySource[s.id] > 0);
  if (!present.length) return <span className="text-ink-subtle">—</span>;
  return (
    <span className="flex flex-wrap gap-1">
      {present.map((s) => (
        <Badge
          key={s.id}
          title={s.hint}
          className={cn('border bg-surface', SOURCE_STYLES[s.id].text, SOURCE_STYLES[s.id].border)}
        >
          {s.label} {bySource[s.id]}
        </Badge>
      ))}
    </span>
  );
};

const cell = 'border-b border-border px-3 py-2 align-middle text-ink';
const numCell = cn(cell, 'text-right font-mono tabular-nums');

/** Like section 1.1 of the report: the project, its pipeline, the totals and failures by source. */
const ProjectsTable = ({ fr }: { fr: AutomationFullRunDto }) => {
  const { t } = useT();
  return (
    <Card>
      <CardHeader title={t('automation.runs.full.byProject')} />
      <TableWrap>
        <Table>
          <thead>
            <tr>
              <Th>{t('automation.columnProject')}</Th>
              <Th>{t('automation.runs.full.pipeline')}</Th>
              <Th>{t('automation.history.status')}</Th>
              <Th className="text-right">{t('automation.runs.full.tests')}</Th>
              <Th className="text-right">{t('automation.runs.results.passed')}</Th>
              <Th className="text-right">{t('automation.runs.results.failed')}</Th>
              <Th className="text-right">{t('automation.runs.results.skipped')}</Th>
              <Th>{t('automation.runs.full.failuresBySource')}</Th>
            </tr>
          </thead>
          <tbody>
            {fr.projects.map((p) => {
              const c = p.summary?.counts;
              return (
                <tr key={p.projectId}>
                  <td className={cell}>
                    {p.teamId ? (
                      <Link
                        to="/automation/teams/$teamId/projects/$projectId"
                        params={{ teamId: p.teamId, projectId: p.projectId }}
                        search={{ tab: 'dashboard' }}
                        className="font-medium text-ink hover:text-accent-fg hover:underline"
                      >
                        {p.name}
                      </Link>
                    ) : (
                      p.name
                    )}
                  </td>
                  <td className={cn(cell, 'font-mono')}>
                    {p.pipelineId ? (
                      <Link
                        to="/automation/runs/$runId"
                        params={{ runId: p.pipelineId }}
                        search={{ project: p.projectId }}
                        className="text-accent-fg hover:underline"
                      >
                        #{p.pipelineId}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className={cell}>
                    {p.status === 'error' ? (
                      <Badge className="bg-fail-soft text-fail" title={p.error ?? undefined}>
                        {t('automation.runs.full.notStarted')}
                      </Badge>
                    ) : (
                      <AutomationStatusBadge status={p.status} />
                    )}
                  </td>
                  {c && p.summary ? (
                    <>
                      <td className={numCell}>{c.total}</td>
                      <td className={numCell}>{c.passed}</td>
                      <td className={cn(numCell, c.failed ? 'text-fail' : undefined)}>
                        {c.failed}
                      </td>
                      <td className={numCell}>{c.skipped}</td>
                      <td className={cell}>
                        <SourceChips bySource={p.summary.bySource} sources={fr.sources} />
                      </td>
                    </>
                  ) : (
                    <td colSpan={5} className={cn(cell, 'text-ink-muted')}>
                      {p.status === 'error'
                        ? (p.error ?? t('automation.runs.full.noPipeline'))
                        : p.final
                          ? t('automation.runs.full.noReportFinal')
                          : t('automation.runs.full.noReportYet')}
                    </td>
                  )}
                </tr>
              );
            })}
            <tr className="font-semibold">
              <td className={cell}>{t('automation.runs.full.totals')}</td>
              <td className={cell} />
              <td className={cell} />
              <td className={numCell}>{fr.totals.total}</td>
              <td className={numCell}>{fr.totals.passed}</td>
              <td className={numCell}>{fr.totals.failed}</td>
              <td className={numCell}>{fr.totals.skipped}</td>
              <td className={cell}>
                <SourceChips bySource={fr.totals.bySource} sources={fr.sources} />
              </td>
            </tr>
          </tbody>
        </Table>
      </TableWrap>
    </Card>
  );
};

interface MergedGroup {
  key: string;
  source: AutomationFullRunSource;
  defect: AutomationFullRunGroup['defect'];
  cause: AutomationCauseId;
  count: number;
  projects: Array<{ name: string; count: number }>;
  tests: Array<AutomationFullRunGroup['tests'][number] & { project: string }>;
}

/** The groups of all projects together: one register defect — one row, how many tests and in which projects. */
const mergeGroups = (fr: AutomationFullRunDto): Map<AutomationFullRunSource, MergedGroup[]> => {
  const byKey = new Map<string, MergedGroup>();
  for (const p of fr.projects) {
    for (const g of p.summary?.groups ?? []) {
      const key = `${g.source}|${g.defect?.id ?? `cause:${g.cause}`}`;
      let merged = byKey.get(key);
      if (!merged) {
        merged = {
          key,
          source: g.source,
          defect: g.defect,
          cause: g.cause,
          count: 0,
          projects: [],
          tests: [],
        };
        byKey.set(key, merged);
      }
      merged.count += g.count;
      merged.projects.push({ name: p.name, count: g.count });
      for (const test of g.tests)
        if (merged.tests.length < 15) merged.tests.push({ ...test, project: p.name });
    }
  }
  const out = new Map<AutomationFullRunSource, MergedGroup[]>();
  for (const m of byKey.values()) {
    const list = out.get(m.source) ?? [];
    list.push(m);
    out.set(m.source, list);
  }
  for (const list of out.values()) list.sort((a, b) => b.count - a.count);
  return out;
};

const Groups = ({ fr }: { fr: AutomationFullRunDto }) => {
  const { t } = useT();
  const merged = useMemo(() => mergeGroups(fr), [fr]);
  const [open, setOpen] = useState<string | null>(null);
  const unrecognised = fr.totals.bySource.new;

  return (
    <Card>
      <CardHeader title={t('automation.runs.full.byCause')} />
      <CardBody className="space-y-4">
        {unrecognised > 0 ? (
          <p className="flex items-start gap-2 text-[13px] text-block">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            {t('automation.runs.full.unrecognised', { count: unrecognised })}
          </p>
        ) : null}
        {fr.sources
          .filter((s) => merged.has(s.id))
          .map((s) => (
            <section key={s.id} className="space-y-2">
              <h3 className="flex flex-wrap items-center gap-2 text-[13px] font-semibold text-ink">
                <span
                  className={cn('size-2.5 shrink-0 rounded-sm', SOURCE_STYLES[s.id].bar)}
                  aria-hidden="true"
                />
                {s.label} · {fr.totals.bySource[s.id]}
                <span className="font-normal text-ink-muted">{s.hint}</span>
              </h3>
              <ul className="space-y-2">
                {(merged.get(s.id) ?? []).map((g) => {
                  const isOpen = open === g.key;
                  return (
                    <li
                      key={g.key}
                      className={cn(
                        'rounded-lg border',
                        isOpen ? SOURCE_STYLES[s.id].border : 'border-border',
                      )}
                    >
                      <button
                        type="button"
                        aria-expanded={isOpen}
                        onClick={() => setOpen(isOpen ? null : g.key)}
                        className="flex w-full items-start justify-between gap-3 px-3 py-2.5 text-left"
                      >
                        <span className="flex min-w-0 items-start gap-2">
                          {isOpen ? (
                            <ChevronDown className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                          ) : (
                            <ChevronRight className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                          )}
                          <Badge className="shrink-0 font-mono">{g.defect?.id ?? '—'}</Badge>
                          <span className="min-w-0">
                            <span className="block text-[13px] text-ink">
                              {g.defect?.title ?? t(`automation.cause.${g.cause}`)}
                            </span>
                            <span className="block text-[12px] text-ink-muted">
                              {g.projects.map((p) => `${p.name} ${p.count}`).join(' · ')}
                            </span>
                          </span>
                        </span>
                        <span className="shrink-0 font-mono text-[13px] font-semibold">
                          {g.count}
                        </span>
                      </button>
                      {isOpen ? (
                        <ul className="space-y-2 px-3 pb-3 pl-9">
                          {g.tests.map((test, index) => (
                            <li key={index} className="space-y-0.5 text-[13px]">
                              <p>
                                <span className="text-ink-subtle">{test.project} ·</span>{' '}
                                <span className="font-mono">{test.className}</span> · {test.name}
                              </p>
                              {test.message ? (
                                <p className="break-words font-mono text-[12px] text-ink-muted">
                                  {test.message}
                                </p>
                              ) : null}
                            </li>
                          ))}
                          {g.count > g.tests.length ? (
                            <li className="text-[12px] text-ink-subtle">
                              {t('automation.runs.full.more', { count: g.count - g.tests.length })}
                            </li>
                          ) : null}
                        </ul>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
      </CardBody>
    </Card>
  );
};

/** The QA defect register the classification uses: a known failure gets its number in the summary. */
const KnownDefects = ({ fullRunId }: { fullRunId: string | undefined }) => {
  const { t } = useT();
  const defects = useKnownDefects();
  // the sources' names come with a full run (the same query its summary reads)
  const fullRun = useFullRun(fullRunId);
  if (!defects.data?.length) return null;
  const labels = new Map(fullRun.data?.sources.map((s) => [s.id, s.label]));
  return (
    <CollapsibleCard
      title={t('automation.runs.full.knownDefects', { count: defects.data.length })}
      rememberAs="automationKnownDefects"
    >
      <p className="mb-2 text-[13px] text-ink-muted">
        {t('automation.runs.full.knownDefectsHint')}
      </p>
      <TableWrap>
        <Table>
          <thead>
            <tr>
              <Th>{t('automation.runs.full.defect')}</Th>
              <Th>{t('automation.runs.full.defectTitle')}</Th>
              <Th>{t('automation.runs.full.source')}</Th>
            </tr>
          </thead>
          <tbody>
            {defects.data.map((d) => (
              <tr key={d.id}>
                <td className={cn(cell, 'whitespace-nowrap font-mono')}>{d.id}</td>
                <td className={cell}>{d.title}</td>
                <td className={cell}>
                  <span className="flex items-center gap-2 whitespace-nowrap">
                    <span
                      className={cn('size-2.5 shrink-0 rounded-sm', SOURCE_STYLES[d.source].bar)}
                      aria-hidden="true"
                    />
                    {labels.get(d.source) ?? d.source}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrap>
    </CollapsibleCard>
  );
};
